import { Injectable, inject } from '@angular/core';
import { Storage, ref, uploadBytesResumable } from '@angular/fire/storage';
import {
  MixedSchema,
  toMentionContent,
  toTextContent,
  versionedSchemaToHtml,
} from '@principle-theorem/editor';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  addInteractions,
  Appointment,
  Brand,
  Contact,
  EmailMessage,
  getMentionContactDetails,
  Interaction,
  Media,
  Patient,
  Practice,
  stafferToNamedDoc,
  toMention,
  type IInteractionError,
} from '@principle-theorem/principle-core';
import {
  EmailActionType,
  EmailResourceType,
  IBrand,
  IAppointment,
  IContact,
  IContactEmailContactDetails,
  IEmailAttachment,
  IEmailReplyTokenData,
  IInteractionData,
  IInteractionV2,
  IMedia,
  InteractionType,
  IPatient,
  MentionResourceType,
  type IPrincipleMention,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  asyncForEach,
  CollectionReference,
  DocumentReference,
  filterUndefined,
  Firestore,
  snapshot,
  snapshotDefined,
  uid,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { EditorPresetsService } from '../editor/editor-presets.service';

export interface ISendEmailData {
  subject: string;
  content: string;
  trackingUrl?: string;
}

export interface IEmailFormData {
  subject: string;
  content: MixedSchema;
  practice: WithRef<IPractice>;
  fileAttachments: File[];
  mediaAttachments: WithRef<IMedia>[];
  saveAttachmentsToPatientFiles: boolean;
}

@Injectable()
export class SendEmailService {
  private _editorPresets = inject(EditorPresetsService);

  constructor(
    private _organisation: OrganisationService,
    private _storage: Storage
  ) {}

  async send(
    data: IInteractionData,
    formData: IEmailFormData
  ): Promise<IInteractionError | undefined> {
    const email = await this._getMentionEmail(data.contact);
    if (!email) {
      return { message: 'Could not resolve email address' };
    }

    const emailData = this._toEmailData(formData);

    try {
      const brandRef = Practice.brandDoc(formData.practice);
      const staffer = await snapshotDefined(this._organisation.staffer$);
      const recipient = this._determineRecipient(data);
      const recipientRef = (recipient?.resourceRef ||
        data.contact.resource
          .ref) as DocumentReference<IContactEmailContactDetails>;

      const replyData = recipient
        ? ({
            uid: uid(),
            practiceRef: formData.practice.ref,
            actionType: EmailActionType.Reply,
            ...recipient,
          } as IEmailReplyTokenData)
        : undefined;

      const uploadResults = await this._uploadFileAttachments(
        data,
        { ref: brandRef },
        formData.fileAttachments,
        formData.saveAttachmentsToPatientFiles
      );

      const uploadedFileRefs = uploadResults.map((result) => result.ref);

      const selectedMediaRefs = formData.mediaAttachments.map(
        (media) => media.ref
      );
      const allAttachmentRefs = [...uploadedFileRefs, ...selectedMediaRefs];

      const interactionAttachments = this._toInteractionAttachments(
        uploadResults,
        formData.mediaAttachments,
        formData.saveAttachmentsToPatientFiles
      );

      await Firestore.addDoc(
        Brand.emailCol({ ref: brandRef }),
        EmailMessage.init({
          recipientEmail: email,
          recipientRef,
          practiceEmail: formData.practice.email,
          subject: formData.subject,
          text: emailData.content,
          html: emailData.content,
          practiceRef: formData.practice.ref,
          authorRef: staffer.ref,
          replyData,
          attachments: allAttachmentRefs.length ? allAttachmentRefs : undefined,
        })
      );

      const shouldAddInteraction = ![
        MentionResourceType.Patient,
        MentionResourceType.Contact,
      ].includes(data.contact.resource.type);

      if (shouldAddInteraction) {
        const interaction = await this._getEmailInteraction(
          formData,
          data.contact,
          interactionAttachments
        );
        await addInteractions(data, interaction);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to send email:', error);
      return { message: 'Failed to send email' };
    }
  }

  private _determineRecipient(
    data: IInteractionData
  ): Pick<IEmailReplyTokenData, 'resourceRef' | 'resourceType'> | undefined {
    if (data.contact.resource.type === MentionResourceType.Patient) {
      return {
        resourceRef: data.contact.resource.ref as DocumentReference<IPatient>,
        resourceType: EmailResourceType.Patient,
      };
    }

    if (data.contact.resource.type === MentionResourceType.Contact) {
      return {
        resourceRef: data.contact.resource.ref as DocumentReference<IContact>,
        resourceType: EmailResourceType.Contact,
      };
    }

    if (data.contact.resource.type === MentionResourceType.Appointment) {
      return {
        resourceRef: Appointment.patientRef(
          asReffable(
            data.contact.resource.ref as DocumentReference<IAppointment>
          )
        ),
        resourceType: EmailResourceType.Patient,
      };
    }
  }

  private async _getEmailInteraction(
    formData: IEmailFormData,
    contact: IPrincipleMention,
    attachments?: IEmailAttachment[]
  ): Promise<IInteractionV2> {
    const staffer = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    return Interaction.init({
      type: InteractionType.Email,
      title: [
        toMentionContent(toMention(staffer, MentionResourceType.Staffer)),
        toTextContent(` sent Email to `),
        toMentionContent(contact),
      ],
      owner: stafferToNamedDoc(staffer),
      content: formData.content,
      attachments,
    });
  }

  private _toEmailData(formData: IEmailFormData): ISendEmailData {
    const extensions = this._editorPresets.defaultToHTMLExtensions();
    const content = versionedSchemaToHtml(formData.content, extensions);
    return { subject: formData.subject, content };
  }

  private async _getMentionEmail(
    mention: IPrincipleMention
  ): Promise<string | undefined> {
    const details = await getMentionContactDetails(mention);
    return details?.email;
  }

  private async _uploadFileAttachments(
    data: IInteractionData,
    brand: IReffable<IBrand>,
    files: File[],
    saveAttachmentsToPatientFiles: boolean
  ): Promise<{ file: File; ref: DocumentReference<IMedia> }[]> {
    if (!files.length) {
      return [];
    }

    const mediaCol = saveAttachmentsToPatientFiles
      ? this._getMediaCol(data)
      : Brand.emailAttachmentMediaCol(brand);

    const results = await asyncForEach(files, async (file) => {
      const path = `${Brand.emailAttachmentStoragePath(brand)}/${uid()}/${
        file.name
      }`;
      await uploadBytesResumable(ref(this._storage, path), file);
      const mediaRef = await Firestore.addDoc(
        mediaCol,
        Media.init({
          name: file.name,
          path,
          metadata: {
            type: file.type || 'application/octet-stream',
            size: file.size,
          },
        })
      );
      return { file, ref: mediaRef };
    });

    return results;
  }

  private _getMediaCol(data: IInteractionData): CollectionReference<IMedia> {
    if (data.contact.resource.type === MentionResourceType.Patient) {
      return Patient.mediaCol({
        ref: data.contact.resource.ref as DocumentReference<IPatient>,
      });
    }

    if (data.contact.resource.type === MentionResourceType.Contact) {
      return Contact.mediaCol({
        ref: data.contact.resource.ref as DocumentReference<IContact>,
      });
    }

    if (data.contact.resource.type === MentionResourceType.Appointment) {
      const patientRef = Appointment.patientRef(
        asReffable(data.contact.resource.ref as DocumentReference<IAppointment>)
      );
      return Patient.mediaCol({ ref: patientRef });
    }

    throw new Error(
      `Unsupported resource type for media upload: ${data.contact.resource.type}`
    );
  }

  private _toInteractionAttachments(
    uploadResults: { file: File; ref: DocumentReference<IMedia> }[],
    mediaAttachments: WithRef<IMedia>[],
    savedToPatientFiles: boolean
  ): IEmailAttachment[] | undefined {
    const fileMetadata: IEmailAttachment[] = uploadResults.map(
      ({ file, ref: mediaRef }) => ({
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        mediaRef,
        savedToPatientFiles,
      })
    );

    const mediaMetadata: IEmailAttachment[] = mediaAttachments.map((media) => {
      const metadata = media.metadata as
        | { type?: string; size?: number }
        | undefined;
      return {
        name: media.name,
        type: metadata?.type || 'application/octet-stream',
        size: metadata?.size ?? 0,
        mediaRef: media.ref,
        savedToPatientFiles: true,
      };
    });

    const attachments = [...fileMetadata, ...mediaMetadata];
    return attachments.length ? attachments : undefined;
  }
}
